import moment from 'moment'
import { callApi } from 'utils/api/callApi'
import { FP_ENDPOINTS } from 'apps/fleet-planner/config'
import { LOAD_STATE } from 'apps/fleet-planner/constants/loads'
import { ILoad, IFileCreate, TFileType, IStop, IFile } from 'apps/fleet-planner/types'
import { FORMATS } from 'constants/datetime'
import { mapRemovedFiles, mapRemovedFilesByUrl } from '../fileUtils'
import { addAttachmentsToLoad, addFileTypeToDocumentList } from './documents'

/**
 * Returns array of IStop values
 */
export const normalizeStops = (stops: IStop[]): IStop[] => {
  return stops.map(stop => {
    const initialTZ = stop.displayTimezone || moment.tz.guess()
    return {
      ...stop,
      startAt:
        stop.startAt && stop.startAt.includes('Z')
          ? moment(stop.startAt).tz(initialTZ).format(FORMATS.fleetPlannerApiFormat)
          : stop.startAt,
      endAt:
        stop.endAt && stop.endAt.includes('Z')
          ? moment(stop.endAt).tz(initialTZ).format(FORMATS.fleetPlannerApiFormat)
          : stop.endAt,
      checkedInAt:
        stop.checkedInAt && stop.checkedInAt.includes('Z')
          ? moment(stop.checkedInAt).tz(initialTZ).format(FORMATS.fleetPlannerApiFormat)
          : stop.checkedInAt,
      checkedOutAt:
        stop.checkedOutAt && stop.checkedOutAt.includes('Z')
          ? moment(stop.checkedOutAt).tz(initialTZ).format(FORMATS.fleetPlannerApiFormat)
          : stop.checkedOutAt,
    }
  })
}

/**
 * Adds an attachment to a load
 * @method PUT
 */
export const addAttachments = (
  load: ILoad,
  newFiles: IFileCreate[],
  fileType: TFileType
): Promise<{ shipment: ILoad }> => {
  const newLoad = addAttachmentsToLoad(
    {
      ...load,
      stops: normalizeStops(load.stops),
    },
    newFiles,
    fileType
  )

  return callApi({
    endpoint: FP_ENDPOINTS.loads,
    method: 'PUT',
    id: load.id,
    body: { shipment: newLoad },
  })
}

/**
 * Adds an attachment to a load, combining multiple POD files into a single PDF
 * @method PUT
 */
export const addAttachmentWithCombinedPods = (
  load: ILoad,
  newFiles: IFileCreate[],
  fileType: TFileType
): Promise<{ shipment: ILoad }> => {
  if (fileType !== 'pod' || load.id === undefined) {
    return addAttachments(load, newFiles, fileType)
  }
  const loadId = Number.parseInt(load.id, 10)
  return callApi({
    endpoint: FP_ENDPOINTS.podUpload(loadId),
    method: 'PUT',
    body: { attachments: newFiles },
  }).then(() => {
    return callApi({
      endpoint: FP_ENDPOINTS.loads,
      method: 'GET',
      id: load.id,
    })
  })
}

/**
 * Replaces an attachment with a new one on a load
 * @method PUT
 */
export const replaceAttachment = (
  replacedUrl: IFile['url'],
  load: ILoad,
  newFiles: IFileCreate[],
  fileType: TFileType
): Promise<{ shipment: ILoad }> => {
  const { pods = [], attachments = [] } = load || {}
  const privateAttachments = addFileTypeToDocumentList(attachments, 'private')
  const podAttachments = addFileTypeToDocumentList(pods, 'pod')
  const newAttachments = addFileTypeToDocumentList(newFiles, fileType)
  const allAttachments = [...privateAttachments, ...podAttachments, ...newAttachments]
  const attachmentsWithoutRemoved = mapRemovedFilesByUrl(allAttachments, replacedUrl)

  const newLoad = {
    ...load,
    stops: normalizeStops(load.stops),
    allAttachments: [...attachmentsWithoutRemoved],
  }

  return callApi({
    endpoint: FP_ENDPOINTS.loads,
    method: 'PUT',
    id: load.id,
    body: { shipment: newLoad },
  })
}

/**
 * Replaces an attachment with a new one on a load, combining multiple POD files into a single PDF
 * @method PUT
 */
export const replaceAttachmentWithCombinedPods = (
  replacedUrl: IFile['url'],
  load: ILoad,
  newFiles: IFileCreate[],
  fileType: TFileType
): Promise<{ shipment: ILoad }> => {
  if (fileType !== 'pod' || load.id === undefined) {
    return replaceAttachment(replacedUrl, load, newFiles, fileType)
  }
  const loadId = Number.parseInt(load.id, 10)
  return callApi({
    endpoint: FP_ENDPOINTS.podUpload(loadId),
    method: 'PUT',
    body: { attachments: newFiles },
  }).then(() => {
    return callApi({
      endpoint: FP_ENDPOINTS.loads,
      method: 'GET',
      id: load.id,
    })
  })
}

/**
 * Removes an attachment by id for a load
 * @method PUT
 */
export const deleteAttachment = (deletedId: number, load: ILoad): Promise<{ shipment: ILoad }> => {
  const { pods = [], attachments = [] } = load
  const privateAttachments = addFileTypeToDocumentList(attachments, 'private')
  const podAttachments = addFileTypeToDocumentList(pods, 'pod')
  const allAttachments = [...privateAttachments, ...podAttachments]
  const attachmentsWithoutRemoved = mapRemovedFiles(allAttachments, deletedId)

  const newLoad = {
    ...load,
    stops: normalizeStops(load.stops),
    allAttachments: [...attachmentsWithoutRemoved],
  }

  return callApi({
    endpoint: FP_ENDPOINTS.loads,
    method: 'PUT',
    id: load.id,
    body: { shipment: newLoad },
  })
}

/**
 * Updates stops on a load
 * @method PUT
 */
export const updateStops = (stops: IStop[], load: ILoad): Promise<{ shipment: ILoad }> => {
  const newLoad = {
    ...load,
    stops: normalizeStops(stops),
  }

  return callApi({
    endpoint: FP_ENDPOINTS.loads,
    method: 'PUT',
    id: load.id,
    body: { shipment: newLoad },
  })
}

/**
 * Updates status on a load
 * @method PUT
 */
export const updateLoadStatus = (
  loadId: ILoad['id'],
  status: LOAD_STATE,
  currentStopSequence: string | null,
  eta?: string
): Promise<{ shipment: ILoad }> => {
  return callApi({
    endpoint: FP_ENDPOINTS.loads,
    method: 'PUT',
    id: loadId,
    group: 'status',
    body: {
      currentStopSequence,
      status,
      eta,
    },
  })
}
